import { useCallback, useEffect, useState } from 'react';

const PARAM = 'league';

function readFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const id = params.get(PARAM);
  return id && id.trim() ? id.trim().toLowerCase() : null;
}

/**
 * Tracks the selected league id via the `?league=` query param.
 * Returns `null` when no league is selected (caller should show a selection screen).
 */
export function useLeagueId(): [string | null, (id: string | null) => void] {
  const [id, setId] = useState<string | null>(readFromUrl);

  // Keep state in sync with browser back/forward navigation.
  useEffect(() => {
    const onPop = () => setId(readFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const set = useCallback((next: string | null) => {
    const url = new URL(window.location.href);
    if (next) {
      url.searchParams.set(PARAM, next);
    } else {
      url.searchParams.delete(PARAM);
    }
    window.history.pushState({}, '', url.toString());
    setId(next);
  }, []);

  return [id, set];
}
